'use client'

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

/* ─── Types ────────────────────────────────────────────────────────────── */
export interface CartCustomization {
  groupId:    string
  groupName:  string
  optionId:   string
  optionName: string
  priceAddon: number
}

export interface FototaartData {
  sizeLabel:  string   // e.g. "9 personen"
  imageData:  string   // base64 data URL ('' for taarten zonder foto)
  text:       string   // optional text on cake
  candles:    number
  notes?:     string   // vrije opmerking van de klant bij deze taart
}

export interface CartItem {
  cartKey:        string            // unique key: productId or productId:opt1:opt2 or FOTOTAART:size
  productId:      string            // 'FOTOTAART' for fototaart items
  name:           string
  price:          number            // base price
  salePrice:      number | null
  image:          string
  quantity:       number
  slug:           string
  customizations?: CartCustomization[]
  fototaartData?:  FototaartData
  // For bundles: short "2× Croissant" lines so the cart can show what's inside
  bundleContents?: string[]
  // Effective ordering limits captured when the item was added, so the cart
  // can cap the +/- and show the "max online" notice. Optional/best-effort —
  // the order API re-resolves and enforces them server-side regardless.
  maxOnlineQty?:  number
  // Minimum quantity per order (seizoen rule: e.g. speculaas hartjes buiten
  // het seizoen alleen vanaf 100 stuks).
  minQty?:        number
  // Lead-time override in OPEN days for this product (e.g. bruidstaart: 2).
  // The checkout calendar blocks earlier dates; the API enforces it too.
  minLeadDays?:   number
  // Mag de bakkerij dit product in punten snijden? Best-effort meegegeven bij
  // het toevoegen, zodat de winkelwagen de gratis snij-optie kan aanbieden.
  // De order-API kijkt zelf naar het product in de database.
  canBeSliced?:   boolean
  // Wat het snijden kost volgens Beheer op het moment van toevoegen (0 = gratis).
  slicingPrice?:  number
}

export type CartNotification = CartItem & { notifId: string; addedQty: number }

interface CartStore {
  items:               CartItem[]
  isOpen:              boolean
  lastAdded:           CartItem | null
  notifications:       CartNotification[]
  /** Voeg toe aan de wagen. `silent` onderdrukt het meldingskaartje onder de
      winkelwagen in de navbar — gebruikt wanneer de klant al ín de winkelwagen
      staat en de toevoeging daar meteen ziet gebeuren. */
  addItem:             (item: Omit<CartItem, 'quantity'>, qty?: number, opts?: { silent?: boolean }) => void
  removeItem:          (cartKey: string) => void
  decrementItem:       (cartKey: string, qty: number) => void
  updateQuantity:      (cartKey: string, quantity: number) => void
  /** Wijzig een bestaande regel (bijv. waardebon-verpakking aan/uit). Als de
      nieuwe cartKey al bestaat worden de aantallen samengevoegd. */
  updateItem:          (cartKey: string, updates: Partial<CartItem>) => void
  clearCart:           () => void
  toggleCart:          () => void
  openCart:            () => void
  closeCart:           () => void
  clearLastAdded:      () => void
  addNotification:     (item: CartItem) => void
  removeNotification:  (notifId: string) => void
  totalItems:          () => number
  totalPrice:          () => number
}

/* ─── Helpers ───────────────────────────────────────────────────────────── */
export function itemEffectivePrice(item: Pick<CartItem, 'price' | 'salePrice' | 'customizations'>): number {
  const base = item.salePrice ?? item.price
  const addons = item.customizations?.reduce((sum, c) => sum + c.priceAddon, 0) ?? 0
  return base + addons
}

/* ─── Store ─────────────────────────────────────────────────────────────── */
export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items:         [],
      isOpen:        false,
      lastAdded:     null,
      notifications: [],

      addItem: (item, qty = 1, opts) => {
        const amount = Math.max(1, Math.floor(qty))
        set((state) => {
          const existing = state.items.find((i) => i.cartKey === item.cartKey)
          let addedItem: CartItem
          let newItems: CartItem[]
          if (existing) {
            addedItem = { ...existing, quantity: existing.quantity + amount }
            newItems  = state.items.map((i) =>
              i.cartKey === item.cartKey ? { ...i, quantity: i.quantity + amount } : i
            )
          } else {
            addedItem = { ...item, quantity: amount }
            newItems  = [...state.items, addedItem]
          }
          if (opts?.silent) {
            return { lastAdded: addedItem, items: newItems, notifications: state.notifications }
          }
          const notif: CartNotification = { ...addedItem, notifId: String(Date.now() + Math.random()), addedQty: amount }
          return {
            lastAdded:     addedItem,
            items:         newItems,
            notifications: [...state.notifications, notif],
          }
        })
      },

      clearLastAdded: () => set({ lastAdded: null }),

      addNotification: (item) => {
        const notif: CartNotification = { ...item, notifId: String(Date.now() + Math.random()), addedQty: item.quantity }
        set((state) => ({ notifications: [...state.notifications, notif] }))
      },

      removeNotification: (notifId) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.notifId !== notifId),
        })),

      removeItem: (cartKey) =>
        set((state) => ({
          items: state.items.filter((i) => i.cartKey !== cartKey),
        })),

      decrementItem: (cartKey, qty) =>
        set((state) => ({
          items: state.items.flatMap((i) => {
            if (i.cartKey !== cartKey) return [i]
            const next = i.quantity - Math.max(1, Math.floor(qty))
            return next > 0 ? [{ ...i, quantity: next }] : []
          }),
        })),

      updateQuantity: (cartKey, quantity) =>
        set((state) => ({
          items:
            quantity <= 0
              ? state.items.filter((i) => i.cartKey !== cartKey)
              : state.items.map((i) =>
                  i.cartKey === cartKey ? { ...i, quantity } : i
                ),
        })),

      updateItem: (cartKey, updates) =>
        set((state) => {
          const current = state.items.find((i) => i.cartKey === cartKey)
          if (!current) return {}
          const updated = { ...current, ...updates }
          const collision = updated.cartKey !== cartKey &&
            state.items.some((i) => i.cartKey === updated.cartKey)
          if (collision) {
            // Merge into the existing row with the same key
            return {
              items: state.items
                .filter((i) => i.cartKey !== cartKey)
                .map((i) =>
                  i.cartKey === updated.cartKey
                    ? { ...i, quantity: i.quantity + updated.quantity }
                    : i
                ),
            }
          }
          return {
            items: state.items.map((i) => (i.cartKey === cartKey ? updated : i)),
          }
        }),

      clearCart: () => set({ items: [] }),

      toggleCart: () => set((state) => ({ isOpen: !state.isOpen })),
      openCart:   () => set({ isOpen: true  }),
      closeCart:  () => set({ isOpen: false }),

      totalItems: () =>
        get().items.reduce((sum, i) => sum + i.quantity, 0),

      totalPrice: () =>
        get().items.reduce(
          (sum, i) => sum + itemEffectivePrice(i) * i.quantity,
          0
        ),
    }),
    {
      name:       'voortman-cart',
      storage:    createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items }),
    }
  )
)
